import {
  Component,
  ViewEncapsulation,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { SubmitOnEnterDirective } from 'app/directives/submit-on-enter/submit-on-enter.directive';

/** Input for {@link ProjectNameDialogComponent}. */
export interface ProjectNameDialogData {
  /** Dialog heading, e.g. `'New project'`. */
  title: string;
  /** Label of the confirming button, e.g. `'Create'`. */
  confirmLabel: string;
  /** Pre-filled name — set when renaming, omitted when creating. */
  name?: string;
  /**
   * A suggested name for a new project. Unlike `name` it is not "the current
   * name", so confirming it unchanged still creates the project.
   */
  initialName?: string;
}

/**
 * Asks for a project name, and does nothing else.
 *
 * Both project actions that need a name — create and rename — use this one
 * dialog so the two are visually identical, which is the whole point of the
 * shared card menu.
 *
 * It deliberately does **not** call the API itself. The caller owns the
 * request and reports failures through `DialogService.showErrorMessage`, which
 * is how every destructive/mutating Fleet flow in the console already works
 * (see `server-configs.component.ts`). Keeping the dialog dumb also keeps it
 * free of any knowledge of which backend owns projects in this build.
 *
 * Closes with the trimmed name, or `undefined`/`null` when dismissed.
 */
@Component({
  selector: 'project-name-dialog',
  templateUrl: './project-name-dialog.component.html',
  styleUrls: ['./project-name-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SubmitOnEnterDirective,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
})
export class ProjectNameDialogComponent {
  readonly data = inject<ProjectNameDialogData>(MAT_DIALOG_DATA);
  readonly matDialogRef =
    inject<MatDialogRef<ProjectNameDialogComponent, string>>(MatDialogRef);

  /**
   * Built here rather than in `ngOnInit` so the template needs no `@if (form)`
   * around the input. A field inside a control-flow block lives in an embedded
   * view, which is a bad place for the one element the dialog wants to focus —
   * and the same structural rule that stops content projection from seeing it.
   */
  readonly form = inject(FormBuilder).group({
    // 255 is Fleet's cap on an app name (`StoreAppRequest.name`), and the lower
    // of the two backends' limits.
    name: [
      this.data.name ?? this.data.initialName ?? '',
      [Validators.required, Validators.maxLength(255)],
    ],
  });

  /** Close without a result. */
  discard(): void {
    this.matDialogRef.close();
  }

  /**
   * Hand the trimmed name back to the caller.
   *
   * A rename to the name it already has is treated as a dismissal — there is
   * nothing to send, and the caller would otherwise show a "renamed" toast for
   * a request that changed nothing.
   */
  confirm(): void {
    const name = String(this.form.value.name ?? '').trim();
    if (!name || name === this.data.name) {
      this.matDialogRef.close();
      return;
    }

    this.matDialogRef.close(name);
  }
}
